/** Filings - the place (ADR-0212 decision 6): one tab per supported regime, and a filing opened.
 *
 * **The tabs are read, not listed.** A tab exists for each regime the product has loaded
 * (`GET /regimes`, whose rows carry the level the product publishes for it), so a regime that is only
 * research tracked has no tab, no row and no logo, and a regime loaded as data tomorrow appears here
 * without an edit. Nothing in this file names a regime. The support chip on a filing is the server's
 * word (`support.py`), never restated.
 *
 * The tab is the `tab` parameter while no filing is open (a filing's own `tab` names one of its long
 * views), so a regime's tab is a link. A regime with one filing opens it directly; with several, the
 * tab is a table of them, each row a compact path, its percentage and the one thing in its way.
 */

import { Flag } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useQueries } from "@tanstack/react-query";

import { filingChecksQuery } from "../../api/filingChecks";
import type { FilingChecks } from "../../api/filingChecks";
import type { Obligation, Scope2Method } from "../../api/queries";
import { useObligations } from "../../api/queries";
import { regimeLabel, useRegimes } from "../../api/regimes";
import type { Regime } from "../../api/regimes";
import { ReadFailed } from "../../components/Loaded";
import { OutsideEngagement } from "../../components/OutsideEngagement";
import { EmptyState } from "../../components/ui/EmptyState";
import { GridTable } from "../../components/ui/GridTable";
import type { GridColumn } from "../../components/ui/GridTable";
import { SectionTabs } from "../../components/ui/SectionTabs";
import { SkeletonRows } from "../../components/ui/Skeleton";
import { StatusChip } from "../../components/ui/StatusChip";
import { FilingStepper } from "../../components/ui/FilingStepper";
import { TablePage } from "../../components/ui/TablePage";
import { supportLevelWords } from "../../lib/customerWords";
import { shortDate } from "../../lib/format";
import { DueNote } from "../DueNote";
import { stepsFromChecks } from "../filingSpine";
import { countsDown } from "../filingState";
import { daysBetween, isoToday } from "../readinessModel";
import { isOwed, orderFilings, standingOf } from "../reportsModel";
import { NoObligations } from "./NoObligations";
import { FilingPage } from "./FilingPage";
import styles from "./Filings.module.css";
import { ServerNote } from "./ServerNote";
import { regimeTabs } from "./regimeTabs";
import { headlineWord, percentText } from "./words";

/** The law's short label from the regimes read ("UK SECR"), or the obligation's own name for it when
 * that read has not answered: a filing must still be titled. */
function filingTitle(filing: Obligation, regimes: readonly Regime[] | undefined): string {
  return `${regimeLabel(filing.regime_key, regimes) === filing.regime_key ? filing.regime_name : regimeLabel(filing.regime_key, regimes)}, ${filing.legal_entity_name}`;
}

function Row({
  filing,
  read,
}: {
  filing: Obligation;
  read: { data?: FilingChecks; error: unknown; isPending: boolean } | undefined;
}) {
  if (!isOwed(filing)) {
    const standing = standingOf(filing, { state: "not_asked" });
    return <StatusChip tone={standing.tone} size="sm">{standing.label}</StatusChip>;
  }
  if (!read || read.isPending) return <span className={styles.refused}>Reading</span>;
  if (read.error || !read.data) return <span className={styles.refused}>Not read</span>;
  const verdict = headlineWord(read.data.summary.verdict, read.data.filing_state);
  return (
    <span className={styles.actionRow}>
      <strong data-testid="row-percent">{percentText(read.data.summary.readiness_percent)}</strong>
      <StatusChip tone={verdict.tone} size="sm">{verdict.word}</StatusChip>
    </span>
  );
}

function RegimeList({
  filings,
  method,
  onOpen,
}: {
  filings: readonly Obligation[];
  method: Scope2Method;
  onOpen: (id: string) => void;
}) {
  const owed = filings.filter(isOwed);
  const reads = useQueries({ queries: owed.map((filing) => filingChecksQuery(filing.obligation_id, method)) });
  const today = isoToday(new Date());
  const readOf = (filing: Obligation) => {
    const at = owed.findIndex((o) => o.obligation_id === filing.obligation_id);
    return at === -1 ? undefined : reads[at];
  };
  const columns: GridColumn<Obligation>[] = [
    {
      id: "entity",
      header: "Company",
      cell: (filing) => (
        <button type="button" className={styles.rowLink} onClick={() => onOpen(filing.obligation_id)} data-testid="filing-open">
          {filing.legal_entity_name}
        </button>
      ),
    },
    { id: "year", header: "Year end", cell: (filing) => shortDate(filing.financial_year_end) },
    {
      id: "due",
      header: "Due",
      // A date that has passed says so (U3): the danger chip with the days it is late, else "in 8 days".
      // An issued filing and a regime that is not law yet are not counted down to.
      cell: (filing) => {
        if (!isOwed(filing) || !filing.due_date) return "–";
        const state = readOf(filing)?.data?.filing_state;
        // No countdown beside an issued filing, in any of its four states: the state is the server's.
        const counted = !filing.in_force_caveat && !filing.in_force_label && (!state || countsDown(state));
        return (
          <DueNote
            date={filing.due_date}
            days={counted ? daysBetween(today, filing.due_date) : undefined}
            testId="filing-due"
          />
        );
      },
    },
    { id: "ready", header: "Ready", cell: (filing) => <Row filing={filing} read={readOf(filing)} /> },
    {
      id: "path",
      header: "Path",
      cell: (filing) => {
        const data = readOf(filing)?.data;
        if (!isOwed(filing) || !data) return "–";
        // Five dots and the one station the filing is at, in words: the five chevron labels were cut
        // to "D.. F.. A.. D.. I.." in a table cell (design review 2026-11-03).
        return (
          <FilingStepper
            steps={stepsFromChecks(data.parts, data.summary.verdict, data.filing_state)}
            label={`Path, ${filing.legal_entity_name}`}
          />
        );
      },
    },
  ];
  return (
    <GridTable
      caption="Filings under this law"
      columns={columns}
      rows={filings}
      rowKey={(filing) => filing.obligation_id}
      rowNumbers={false}
      onRowOpen={(filing) => onOpen(filing.obligation_id)}
      testId="filings-table"
    />
  );
}

export function FilingsPlace({ method, filingId, tab }: { method: Scope2Method; filingId: string | undefined; tab: string | undefined }) {
  const navigate = useNavigate();
  const obligations = useObligations();
  const regimes = useRegimes();

  const open = (id: string) =>
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, filing: id, tab: undefined }),
    });
  const pick = (key: string) =>
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, filing: undefined, tab: key }),
    });

  // A failed read of the laws is shown as one: the tabs then come from the filings alone, and the
  // screen says it is working from less rather than looking complete.
  const regimesFailed = regimes.error ? (
    <ReadFailed of="The list of laws" error={regimes.error} onRetry={() => void regimes.refetch()} />
  ) : null;

  if (obligations.error) {
    return (
      <TablePage
        icon={Flag}
        title="Filings"
        state={
          <ReadFailed
            of="This workspace's filing obligations"
            error={obligations.error}
            onRetry={() => void obligations.refetch()}
          />
        }
      />
    );
  }
  if (!obligations.data) {
    // The shared loading placeholder, not an empty state: a card titled "Reading filings" is drawn by
    // the same component as "No filings", and a reader cannot tell the two apart (G-P9-09). The
    // words are still said, to a screen reader.
    return <TablePage icon={Flag} title="Filings" state={<SkeletonRows rows={4} label="Reading filings…" />} />;
  }

  // A successful read with no obligation in it is a statement about the company, and it is made in
  // its own words: not a blank, and not the same sentence as a regime with nothing owed under it.
  if (obligations.data.length === 0) {
    return (
      <TablePage
        icon={Flag}
        title="Filings"
        band={regimesFailed}
        state={<NoObligations />}
      />
    );
  }
  const filings = orderFilings(obligations.data);
  const tabs = regimeTabs(regimes.data, filings);
  const asked = filingId ? filings.find((filing) => filing.obligation_id === filingId) : undefined;
  const regimeKey = asked?.regime_key ?? (tabs.some((entry) => entry.key === tab) ? tab : tabs[0]?.key);
  const strip = (
    <>
      {regimesFailed}
      {filingId && !asked ? <OutsideEngagement noun="filing" testId="filing-not-offered" /> : null}
    <SectionTabs
      label="Laws"
      testId="regime-tabs"
      tabs={tabs.map((entry) => ({ key: entry.key, label: entry.label, current: entry.key === regimeKey }))}
      onSelect={pick}
    />
    </>
  );
  const inRegime = filings.filter((filing) => filing.regime_key === regimeKey);
  // The filings that exist come first (G-Wviewer-10): a regime's tab listed nine rows, eight of them
  // years or companies that are not owed or not yet determined, each with a dash for Due and Path. Those
  // are Applicability's rows; here they are one line.
  const owedHere = inRegime.filter(isOwed);
  const notOwedHere = inRegime.length - owedHere.length;
  const loaded = regimes.data?.find((regime) => regime.key === regimeKey);

  // A link that named a filing this workspace does not have opens nothing in its place: a lone
  // filing is not opened for it, because that is the silent substitution the notice exists to refuse.
  const target = asked ?? (!filingId && inRegime.length === 1 ? inRegime[0] : undefined);
  if (target) {
    return <FilingPage active={target} method={method} title={filingTitle(target, regimes.data)} lead={strip} />;
  }

  return (
    <TablePage
      icon={Flag}
      title="Filings"
      label={loaded?.label}
      band={strip}
      actions={
        loaded ? (
          <ServerNote label={supportLevelWords(loaded.support_level)} exact={loaded.support_level ?? undefined} title="Support level" text={loaded.support_because} testId="support-chip" />
        ) : undefined
      }
      {...(owedHere.length === 0
        ? {
            state: (
              <EmptyState title="Nothing is owed under this law" compact testId="regime-empty" />
            ),
          }
        : { table: <RegimeList filings={owedHere} method={method} onOpen={open} /> })}
      {...(notOwedHere > 0
        ? {
            after: (
              <p className={styles.note} data-testid="regime-not-owed">
                {notOwedHere} other {notOwedHere === 1 ? "year or company is" : "years or companies are"} not
                owed or not yet determined (Applicability lists them).
              </p>
            ),
          }
        : {})}
    />
  );
}
